import React from 'react';
import { ArrowLeft, Cookie, Shield, Lock, ExternalLink } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { appPath } from '../utils/appRouting';

const CookiePolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(appPath('/'))}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Torna alla Home</span>
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg flex items-center justify-center">
                <span className="text-yellow-400 font-bold text-sm">S+</span>
              </div>
              <span className="text-sm font-semibold text-foreground hidden sm:block">SOSTEGNO PRO+</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Title Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mb-4">
            <Cookie className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Cookie Policy</h1>
          <p className="text-muted-foreground">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>
        </div>

        {/* Content */}
        <div className="bg-card rounded-2xl shadow-sm border border-border overflow-hidden">
          <div className="p-6 md:p-8 space-y-8">
            
            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">1</span>
                Cosa sono i Cookie
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                I cookie sono piccoli file di testo che i siti web visitati dall'utente inviano al suo terminale 
                (computer, tablet, smartphone), dove vengono memorizzati per essere poi ritrasmessi agli stessi siti 
                alla successiva visita. I cookie sono utilizzati per diverse finalità, hanno caratteristiche diverse 
                e possono essere utilizzati sia dal titolare del sito che si sta visitando, sia da terze parti.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">2</span>
                Tipologie di Cookie Utilizzati
              </h2>
              
              <div className="space-y-4">
                <div className="bg-muted/40 rounded-xl p-5 border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Cookie Tecnici (Necessari)</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        Questi cookie sono essenziali per il corretto funzionamento della piattaforma SOSTEGNO PRO+. 
                        Non richiedono il consenso dell'utente.
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Mantengono l'utente autenticato durante la sessione</li>
                        <li>Durata: Sessione o 7 giorni (se "Ricordami" è selezionato)</li>
                        <li>Necessari per accedere alle funzionalità riservate</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/40 rounded-xl p-5 border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Cookie className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Cookie Funzionali</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        Questi cookie permettono di ricordare le scelte effettuate dall'utente e fornire funzionalità avanzate.
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Memorizzano le preferenze dell'interfaccia utente</li>
                        <li>Ricordano le impostazioni di visualizzazione</li>
                        <li>Durata: 1 anno</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-muted/40 rounded-xl p-5 border border-border">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Lock className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground mb-1">Cookie di Sicurezza</h3>
                      <p className="text-muted-foreground text-sm mb-3">
                        Cookie utilizzati per proteggere l'account dell'utente e prevenire accessi non autorizzati.
                      </p>
                      <ul className="text-sm text-muted-foreground space-y-1 ml-4 list-disc">
                        <li>Protezione CSRF (Cross-Site Request Forgery)</li>
                        <li>Verifica dell'integrità delle sessioni</li>
                        <li>Rilevamento di attività sospette</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">3</span>
                Cookie di Terze Parti
              </h2>
              <div className="bg-muted/40 rounded-xl p-5 border border-border">
                <h4 className="font-semibold text-foreground mb-2">Firebase Authentication</h4>
                <p className="text-muted-foreground text-sm mb-3">
                  Utilizzato per la gestione sicura dell'autenticazione degli utenti.
                </p>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>Provider:</strong> Google Firebase</p>
                  <p><strong>Finalità:</strong> Autenticazione e gestione sessioni</p>
                  <p><strong>Durata:</strong> Variabile (sessione o persistente)</p>
                  <p>
                    <strong>Privacy Policy:</strong>{' '}
                    <a 
                      href="https://firebase.google.com/support/privacy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1"
                    >
                      firebase.google.com <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">4</span>
                Cookie NON Utilizzati
              </h2>
              <p className="text-muted-foreground mb-3">SOSTEGNO PRO+ <strong>NON</strong> utilizza:</p>
              <ul className="text-muted-foreground space-y-2 ml-4 list-disc">
                <li>Cookie di profilazione per pubblicità mirata</li>
                <li>Cookie di tracciamento per analisi comportamentali dettagliate</li>
                <li>Cookie di social network (eccetto se esplicitamente integrati)</li>
                <li>Cookie per la vendita di dati a terze parti</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">5</span>
                Gestione dei Cookie
              </h2>
              <p className="text-muted-foreground mb-4">
                L'utente può gestire i cookie attraverso le impostazioni del proprio browser:
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { name: 'Chrome', url: 'https://support.google.com/chrome/answer/95647' },
                  { name: 'Firefox', url: 'https://support.mozilla.org/it/kb/Gestione%20dei%20cookie' },
                  { name: 'Safari', url: 'https://support.apple.com/it-it/guide/safari/sfri11471/mac' },
                  { name: 'Edge', url: 'https://support.microsoft.com/it-it/microsoft-edge' }
                ].map((browser) => (
                  <a
                    key={browser.name}
                    href={browser.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-muted/40 hover:bg-muted/60 rounded-xl p-3 text-center text-sm font-medium text-muted-foreground transition-colors border border-border"
                  >
                    {browser.name}
                  </a>
                ))}
              </div>
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-amber-800 text-sm">
                  <strong>Attenzione:</strong> La disabilitazione dei cookie tecnici può compromettere o impedire 
                  l'utilizzo della piattaforma SOSTEGNO PRO+.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">6</span>
                Riferimenti Normativi
              </h2>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Regolamento UE 2016/679 (GDPR) - Art. 5, 6, 7, 13, 14</li>
                  <li>• Direttiva 2002/58/CE (ePrivacy) - Art. 5(3)</li>
                  <li>• D.Lgs. 196/2003 modificato dal D.Lgs. 101/2018</li>
                  <li>• Provvedimento del Garante Privacy dell'8 maggio 2014</li>
                  <li>• Linee guida cookie e altri strumenti di tracciamento - 10 giugno 2021</li>
                </ul>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-foreground mb-3 flex items-center gap-2">
                <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 text-sm font-bold">7</span>
                Contatti
              </h2>
              <p className="text-muted-foreground">
                Per domande sulla presente Cookie Policy, contattaci:{' '}
                <a href="mailto:privacy@sostegnopro.it" className="text-blue-600 hover:underline font-medium">
                  privacy@sostegnopro.it
                </a>
              </p>
            </section>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <Link to={appPath('/privacy')} className="hover:text-blue-600 transition-colors">Privacy Policy</Link>
          <Link to={appPath('/terms')} className="hover:text-blue-600 transition-colors">Termini e Condizioni</Link>
          <Link to={appPath('/note-legali-ai')} className="hover:text-blue-600 transition-colors">Note legali AI</Link>
          <Link to={appPath('/')} className="hover:text-blue-600 transition-colors">Home</Link>
        </div>
      </main>
    </div>
  );
};

export default CookiePolicy;
